
<template>
  <v-container fluid class="pa-4">
    <v-container fluid class="pa-4">
      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <v-card class="mx-auto" max-width="100%">
        <v-card-title class="d-flex justify-start align-center">
  
          <!-- ✅ Portfolio Info -->
          <div v-if="portfolioInfo" class="ml-4">
            <span class="font-weight-bold">{{ portfolioInfo.portfolioName }}</span>
            <span class="ml-2 text-caption">
              ({{ formatDate(portfolioInfo.portfolioStart) }}
              – {{ portfolioInfo.portfolioEnd ? formatDate(portfolioInfo.portfolioEnd) : 'Active' }})
            </span>
            <span class="ml-4 text-h6" :class="getColorClass(portfolioInfo.pnl)">
              ₹{{ Number(portfolioInfo.pnl).toFixed(0) }}
            </span>
          </div>
  
          <v-btn color="secondary" icon variant="text" size="large" title="Refresh PnL" class="ml-1" @click="refreshPnL">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
  
          <!-- keep your existing total pnl display 
                                    <span class="ml-4 text-h6" :class="getColorClass(totalPnl)">
                                        (Total: ₹{{ totalPnl.toFixed(0) }})
                                    </span>-->
          <v-btn variant="text" color="primary" @click="showMarginDialog = true">
            Margin
          </v-btn>
          <!-- 📌 New Close Portfolio Button -->
          <v-menu v-model="showCloseMenu" :close-on-content-click="false" offset-y>
            <template #activator="{ props }">
              <v-btn variant="text" color="red" v-bind="props">
                Close Portfolio
              </v-btn>
            </template>
            <v-card class="pa-3" max-width="300">
              <v-date-picker v-model="closeDate" />
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="showCloseMenu = false">Cancel</v-btn>
                <v-btn color="red" text @click="closePortfolio">Confirm</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
          <v-spacer></v-spacer>
          <NiftyLevel v-slot="{ currentLevel }">
            <span v-if="portfolioInfo?.niftyLevelAtStart && currentLevel" class="ml-2">
              &nbsp;&nbsp;&nbsp;&nbsp;|| &nbsp;Start Level: {{ portfolioInfo.niftyLevelAtStart }}
              <span :class="Number(currentLevel - portfolioInfo.niftyLevelAtStart) < 0 ? 'text-red' : 'text-green'">
                [{{ (currentLevel - portfolioInfo.niftyLevelAtStart).toFixed(0) }}]
              </span>
            </span>
          </NiftyLevel>
        </v-card-title>
  
        <v-card-text>
  
          <v-row v-for="(trade, index) in allTrades" :key="index" class="mb-1">
            <!-- Info Cards -->
            <v-col cols="12" md="3" class="pa-1">
              <StrategyCard :strategy="trade.strategyInfo" />
              <div v-if="trade.showChart && trade.chartData?.labels?.length">
                <v-btn icon variant="text" size="small" color="red" class="mb-1" @click="trade.showChart = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
                <ChartComponent :data="trade.chartData" />
              </div>
              <v-btn v-else icon variant="text" size="small" color="primary" @click="trade.showChart = true">
                <v-icon>mdi-chart-line</v-icon>
              </v-btn>
            </v-col>
  
            <!-- Table -->
            <v-col cols="12" md="9" class="pa-1">
              <v-expansion-panels v-model="expandedPanels" multiple>
                <v-expansion-panel value="0" expanded v-if="trade.positions.some(p => p.positionStatus === 'OPEN')">
                  <v-expansion-panel-text>
                    <v-data-table :headers="headers" :items="trade.positions.filter(p => p.positionStatus === 'OPEN')"
                      :items-per-page="-1" item-value="optionPositionId" hide-default-footer class="elevation-1">
                      <template #item="{ item }">
                        <tr :class="getRowClass(item)">
                          <td>
                            <div class="d-flex align-center">
                              <v-img :src="item.application === 'BREEZE' ? '/icici.png' : '/zerodha.png'"
                                :alt="item.application" :max-width="item.application === 'BREEZE' ? 50 : 25"
                                :max-height="item.application === 'BREEZE' ? 50 : 25" cover />
                            </div>
                          </td>
                          <td>{{ formatDate(item.expiry_date) }}</td>
                          <td>{{ item.strike_price }}</td>
                          <td>{{ item.right }}</td>
                          <td>{{ item.action }}</td>
                          <td>{{ item.quantity }}</td>
                          <td>{{ item.average_price }}</td>
                          <td>{{ item.ltp }}</td>
                          <td>{{ item.positionStatus }}</td>
                          <td>
                            <div class="d-flex align-center">
                              <v-text-field v-model="item.exit_price" variant="outlined" density="compact" hide-details
                                placeholder="Exit Price" class="exit-input"
                                :disabled="item.positionStatus !== 'OPEN' && !item.editing"
                                @change="onExitPriceChange(item)" />
                              <v-icon v-if="item.positionStatus === 'OPEN'" color="primary" size="20"
                                class="cursor-pointer ml-1" @click="updatePositionFromOrder(item)">
                                mdi-refresh
                              </v-icon>
                            </div>
                          </td>
  
                          <td>
                            <span :class="getColorClass(item.position_pnl)">{{ item.position_pnl
                              }}</span>
                          </td>
                          <td>
                            <div class="d-flex align-center">
                              <v-icon v-if="item.positionStatus === 'CLOSED' && !item.editing" color="primary" size="24"
                                class="cursor-pointer mr-2" @click="enableEditing(item)">
                                mdi-pencil
                              </v-icon>
                              <v-icon v-if="item.editing" color="green" size="24" class="cursor-pointer"
                                @click="savePosition(item)">
                                mdi-check
                              </v-icon>
  
                              <v-icon v-if="item.positionStatus === 'OPEN'" color="orange" size="24"
                                class="cursor-pointer ml-2" :disabled="isExitDisabled(item)" @click="confirmExit(item)">
                                mdi-exit-to-app
                              </v-icon>
                            </div>
                          </td>
                        </tr>
                      </template>
                    </v-data-table>
                  </v-expansion-panel-text>
                </v-expansion-panel>
  
                <v-expansion-panel v-if="trade.positions.some(p => p.positionStatus === 'CLOSED')">
                  <v-expansion-panel-title>
                    <strong>Closed Positions</strong>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <v-data-table :headers="headers" :items="trade.positions.filter(p => p.positionStatus === 'CLOSED')"
                      :items-per-page="-1" item-value="optionPositionId" hide-default-footer class="elevation-1">
                      <template #item="{ item }">
                        <tr :class="getRowClass(item)">
                          <td>
                            <div class="d-flex align-center">
                              <v-img :src="item.application === 'BREEZE' ? '/icici.png' : '/zerodha.png'"
                                :alt="item.application" :max-width="item.application === 'BREEZE' ? 50 : 25"
                                :max-height="item.application === 'BREEZE' ? 50 : 25" cover />
                            </div>
                          </td>
                          <td>{{ formatDate(item.expiry_date) }}</td>
                          <td>{{ item.strike_price }}</td>
                          <td>{{ item.right }}</td>
                          <td>{{ item.action }}</td>
                          <td>{{ item.quantity }}</td>
                          <td>{{ item.average_price }}</td>
                          <td>{{ item.ltp }}</td>
                          <td>
                            <v-select v-if="item.editing" v-model="item.positionStatus" :items="['OPEN', 'CLOSED']"
                              density="compact" hide-details style="max-width: 100px" />
                            <span v-else>{{ item.positionStatus }}</span>
                          </td>
                          <td>
                            <v-text-field v-model="item.exit_price"
                              :variant="item.positionStatus === 'OPEN' ? 'outlined' : undefined" density="compact"
                              hide-details placeholder="Exit Price" class="exit-input"
                              :disabled="item.positionStatus !== 'OPEN' && !item.editing"
                              @change="onExitPriceChange(item)" />
                          </td>
                          <td>
                            <span :class="getColorClass(item.position_pnl)">{{ item.position_pnl
                              }}</span>
                          </td>
                          <td>
                            <div class="d-flex align-center">
                              <v-icon v-if="item.positionStatus === 'CLOSED' && !item.editing" color="primary" size="24"
                                class="cursor-pointer mr-2" @click="enableEditing(item)">
                                mdi-pencil
                              </v-icon>
  
                              <template v-if="item.editing">
                                <v-icon color="green" size="24" class="cursor-pointer mr-2" @click="savePosition(item)">
                                  mdi-check
                                </v-icon>
  
                                <v-icon color="red" size="24" class="cursor-pointer" @click="cancelEditing(item)">
                                  mdi-close
                                </v-icon>
                              </template>
                            </div>
                          </td>
                        </tr>
                      </template>
                    </v-data-table>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <!-- 📌 Margin Dialog Starts Here -->
      <v-dialog v-model="showMarginDialog" max-width="800">
        <v-card>
          <v-card-title>Margin Details</v-card-title>
          <v-card-text>
            <v-progress-linear v-if="loadingMargin" indeterminate />
            <v-data-table v-else :headers="marginHeaders" :items="marginData" class="elevation-1" hide-default-footer>
              <template #item="{ item }">
                <tr>
                  <td>
                    <div class="d-flex align-center">
                      <v-img :src="item.application === 'BREEZE' ? '/icici.png' : '/zerodha.png'" :alt="item.application"
                        :max-width="item.application === 'BREEZE' ? 50 : 25"
                        :max-height="item.application === 'BREEZE' ? 50 : 25" cover />
                    </div>
                  </td>
                  <td>{{ formatINR(item.net) }}</td>
                  <td>{{ formatINR(item.cash) }}</td>
                  <td>{{ formatINR(item.live_balance) }}</td>
                  <td>{{ formatINR(item.debits) }}</td>
                  <td>{{ formatINR(item.exposure) }}</td>
                  <td>{{ formatINR(item.span) }}</td>
                </tr>
              </template>
            </v-data-table>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="showMarginDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-container>
</template>
  
<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import NiftyLevel from '@/components/trades/NiftyLevel.vue'
import StrategyCard from '@/components/trades/StrategyCard.vue'
import ChartComponent from '@/components/trades/ChartComponent.vue'
import { useStore } from 'vuex'
import { useDialog } from '@/composables/useDialog'
import { useRoute } from 'vue-router'

const route = useRoute()
const store = useStore()
const { alert, confirm } = useDialog()

const loading = ref(true)
const loadingMargin = ref(false)
const showMarginDialog = ref(false)

const tradeIds = ref([])
const allTrades = ref([])
const expandedPanels = ref([0])
const marginData = ref([])
const portfolioInfo = ref(route.query.portfolioId);


const headers = [
  { title: 'App', key: 'application', headerClass: 'bold-header' },
  { title: 'Expiry', key: 'expiry_date', headerClass: 'bold-header' },
  { title: 'Strike', key: 'strike_price', headerClass: 'bold-header' },
  { title: 'Right', key: 'right', headerClass: 'bold-header' },
  { title: 'Type', key: 'Type', headerClass: 'bold-header' },
  { title: 'Qty', key: 'quantity', headerClass: 'bold-header' },
  { title: 'Buy Price', key: 'average_price', headerClass: 'bold-header' },
  { title: 'LTP', key: 'ltp', headerClass: 'bold-header' },
  { title: 'Status', key: 'positionStatus', headerClass: 'bold-header' },
  { title: 'Exit Price', key: 'exit_price', headerClass: 'bold-header' },
  { title: 'PNL', key: 'pnl', headerClass: 'bold-header' },
  { title: 'Action', key: 'Action', headerClass: 'bold-header', sortable: false }
]

const marginHeaders = [
  { title: 'App', key: 'application' },
  { title: 'Final Balance', key: 'net' },
  { title: 'Available Cash', key: 'cash' },
  { title: 'Current Balance', key: 'live_balance' },
  { title: 'Pending Deductions', key: 'debits' },
  { title: 'Risk Margin', key: 'exposure' },
  { title: 'Required Margin', key: 'span' }
]
const showCloseMenu = ref(false)
const closeDate = ref(new Date().toISOString().substr(0, 10)) // default today
const currentPortfolioId = ref(route.params.portfolioId || route.query.portfolioId || null)
const closePortfolio = async () => {

  console.log('Closing Portfolio ID:', currentPortfolioId.value)
  const hasOpenPositions = allTrades.value.some(trade =>
    trade.positions.some(pos => pos.positionStatus === 'OPEN')
  )
  console.log('Portfolio hasOpenPositions:', hasOpenPositions)
  if (hasOpenPositions) {
    store.commit('snackbar/showMessage', {
      text: 'Cannot close portfolio: there are open positions.',
      color: 'error'
    })
    return
  }

  try {
    await store.dispatch('portfolio/markPortfolioClosed', {
      portfolioId: currentPortfolioId.value,
      portfolioEndDate: closeDate.value,
    })
    store.commit('snackbar/showMessage', {
      text: 'Portfolio closed successfully',
      color: 'success',
    })
    await getPortfolioTrade(currentPortfolioId.value)
  } catch (err) {
    console.error('Failed to close portfolio:', err)
    store.commit('snackbar/showMessage', {
      text: 'Failed to close portfolio',
      color: 'error',
    })
  } finally {
    showCloseMenu.value = false
  }
}

const totalPnl = computed(() => {
  return allTrades.value
    .flatMap(t => t.positions || [])
    .reduce((sum, pos) => sum + (Number(pos.position_pnl) || 0), 0)
})

const formatINR = (num) => {
  if (typeof num !== 'number') return num
  return '₹' + Math.round(num).toLocaleString('en-IN')
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const day = date.getDate().toString().padStart(2, '0')
  const month = date.toLocaleString('en-US', { month: 'short' })
  const year = date.getFullYear().toString().slice(-2)
  return `${day}-${month}-${year}`
}

const getColorClass = (value) => {
  return Number(value) < 0 ? 'text-red' : 'text-green'
}

const getRowClass = (item) => {
  return item.positionStatus === 'CLOSED' ? 'closed-row' : ''
}

const enableEditing = (item) => {
  item.editing = true
}

const cancelEditing = (item) => {
  item.editing = false
}

const onExitPriceChange = (item) => {
  // Optional: validate or debounce if needed
  console.log('Exit price changed:', item.exit_price, item)
}

const isExitDisabled = (item) => {
  return isNaN(item.exit_price) || item.exit_price === null || item.exit_price === ''
}

const confirmExit = (item) => {
  if (!isExitDisabled(item)) {
    confirm({
      title: 'Confirm to Close',
      message: 'Are you sure you want to close this position?',
      onConfirm: async () => { savePosition(item) },
      onCancel: () => { cancelEditing(item) }
    })
  }
}

const savePosition = async (item) => {
  if (item.positionStatus === 'CLOSED' && (item.exit_price == null || item.exit_price === '')) {
    store.commit('snackbar/showMessage', {
      text: 'Please enter exit price before saving',
      color: 'error'
    })
    return
  }

  try {
    loading.value = true
    const payload = {
      optionPositionId: item.optionPositionId,
      status: item.positionStatus,
      exitPrice: item.exit_price === null || item.exit_price === '' ? 0 : Number(item.exit_price)
    }
    await store.dispatch('positions/updateExitPrice', payload)

    await getPortfolioTrade()

    store.commit('snackbar/showMessage', {
      text: 'Position updated successfully',
      color: 'success'
    })
  } catch (err) {
    console.error('Failed to update position:', err)
    store.commit('snackbar/showMessage', {
      text: 'Failed to update position',
      color: 'error'
    })
  } finally {
    loading.value = false
    item.editing = false
  }
}

const getTradePnLHistory = async (tradeId) => {
  try {
    const res = await store.dispatch('trades/getTradePnLHistory', { tradeId })
    return res.tradePlnHistoryList || []
  } catch (e) {
    console.error('Error fetching PnL history:', e)
    return []
  }
}

const formatChartData = (historyList) => {
  if (!Array.isArray(historyList)) {
    return { labels: [], datasets: [] }
  }
  return {
    labels: historyList.map(h => new Date(h.date)),
    datasets: [
      {
        label: 'PNL',
        data: historyList.map(h => h.pnl),
        borderColor: '#e53935',
        backgroundColor: '#ffcdd2',
        tension: 0.3
      },
      {
        label: 'Nifty Level',
        data: historyList.map(h => h.niftyLevel),
        borderColor: '#1e88e5',
        backgroundColor: '#bbdefb',
        tension: 0.3
      }
    ]
  }
}

const getPortfolioTrade = async () => {
  try {
    loading.value = true
    const portfolioId = portfolioInfo.value|| null
    console.log('📦 portfolioId:', portfolioId)
    console.log('📦 portfolioInfo:', portfolioInfo.value)
    const res = await store.dispatch('portfolio/getPortfolioTrade', { portfolioId })

    console.log('📦 getPortfolioTrade response:', res)

    if (res.portoflioTradeDatail) {
      portfolioInfo.value = res.portoflioTradeDatail.porfolioInfo   // ✅ save portfolio info
      currentPortfolioId.value = res.portoflioTradeDatail.porfolioInfo.portfolioId // save the loaded portfolio ID
      if (Array.isArray(res.portoflioTradeDatail.tradeDetailList)) {
        tradeIds.value = res.portoflioTradeDatail.tradeDetailList.map(t => t.tradeId)
        allTrades.value = await Promise.all(
          res.portoflioTradeDatail.tradeDetailList.map(async (t) => {
            const history = await getTradePnLHistory(t.tradeId)
            return {
              strategyInfo: {
                tradeId: t.tradeId,
                strategyName: t.strategyName,
                strategyType: t.strategyType,
                pnl: t.pnl,
                totalDebit: t.totalDebit,
                totalCredit: t.totalCredit,
                totalCreditDebit: t.totalCreditDebit,
                stock_code: t.positionInfoList?.[0]?.stock_code || '',
                segment: t.positionInfoList?.[0]?.segment || '',
                product_type: t.positionInfoList?.[0]?.product_type || ''
              },
              chartData: formatChartData(history),
              positions: t.positionInfoList.map((p) => ({ ...p, strategyName: t.strategyName }))
            }
          })
        )
      } else {
        allTrades.value = []
      }
    }
  } catch (e) {
    console.error('Failed to fetch trade positions:', e)
    allTrades.value = []
  } finally {
    loading.value = false
  }
}


const refreshPnL = async () => {
  try {
    console.log('🔄 Refreshing PnL for portfolio:', route.query.portfolioId)
    loading.value = true
    await store.dispatch('positions/refreshLtpForPositions')
    await store.dispatch('trades/updateTradeListPnL', { tradeIds: tradeIds.value })
    await getPortfolioTrade( route.query.portfolioId)
  } catch (error) {
    console.error('Failed to refresh:', error)
  } finally {
    loading.value = false
  }
}

const updatePositionFromOrder = async (item) => {
  try {
    loading.value = true
    const payload = {
      expiry_date: item.expiry_date,
      strike_price: item.strike_price,
      right: item.right,
      action_type: item.action,   // assuming action is BUY/SELL
      quantity: item.quantity,
    }

    // 🔹 Dispatch store call (replace with your correct action)
    const res = await store.dispatch('positions/fetchExitPriceFromOrder', payload)

    if (res?.exit_price) {
      item.exit_price = res.exit_price
      store.commit('snackbar/showMessage', {
        text: 'Exit price updated from order',
        color: 'success',
      })
    } else {
      store.commit('snackbar/showMessage', {
        text: 'No exit price found',
        color: 'warning',
      })
    }
  } catch (err) {
    console.error('Error updating position from order:', err)
    store.commit('snackbar/showMessage', {
      text: 'Failed to fetch exit price',
      color: 'error',
    })
  } finally {
    loading.value = false
  }
}

watch(showMarginDialog, async (val) => {
  if (val) {
    loadingMargin.value = true
    try {
      const res = await store.dispatch('trades/getMarginList')
      marginData.value = res.marginDetailList || []
    } catch (err) {
      console.error('Failed to load margin data:', err)
    } finally {
      loadingMargin.value = false
    }
  }
})

onMounted(() => {
  getPortfolioTrade()
})
  </script>
  
  <style scoped>
.closed-row {
    background-color: #f0f0f0 !important;
    /* Light gray */
  }
  
  .v-card-text {
    overflow: visible !important;
    max-height: none !important;
    padding: 0;
  }
  
  .exit-input {
    max-width: 100px;
    transition: all 0.2s ease-in-out;
    margin-top: 4px;
  }
  
  .exit-input input {
    text-align: right;
    font-weight: 500;
    font-size: 13px;
    padding: 4px 8px;
    background-color: #fffaf3;
    border-radius: 8px;
  }
  
  /* Alternative way to target headers */
  :deep(th) {
    font-weight: bold !important;
    font-size: 14px !important;
    background-color: #fbeee6 !important;
  }
  
  .cursor-pointer {
    cursor: pointer;
    margin: 0 4px;
  }
  
  /* Style for editable fields */
  .editable-field {
    background-color: #fffaf3;
    border-radius: 4px;
    padding: 0 8px;
  }
  
  .text-red {
    color: #e53935;
  }
  
  .text-green {
    color: #43a047;
  }
  
  :deep(.v-expansion-panel-text__wrapper) {
    padding-top: 4px !important;
    padding-bottom: 4px !important;
  }
  
  /* Reduce gap between rows inside v-data-table */
  :deep(.v-data-table tbody tr) {
    height: 34px;
    /* default is ~48px */
  }
  
  :deep(.v-data-table) {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }
</style>
  